import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { client } from '../api/client'
import { Parent } from '../types'

interface CoParentFormData {
  first_name: string
  last_name: string
  email?: string
  street_name?: string
  street_number?: string
  city?: string
  zip_code?: string
  vat_number?: string
  social_security_number?: string
}

interface CoParentFormProps {
  onSuccess: (coParent: Parent) => void
  onRemove?: () => void
  existingCoParent?: Parent | null
  isNested?: boolean
  parent?: Parent | null
  highlightSsn?: boolean
}

export function CoParentForm({ 
  onSuccess, 
  onRemove, 
  existingCoParent,
  isNested = false,
  parent = null,
  highlightSsn = false
}: CoParentFormProps) {
  const { t } = useTranslation(['profile'])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
  } = useForm<CoParentFormData>({
    defaultValues: {
      first_name: existingCoParent?.first_name || '',
      last_name: existingCoParent?.last_name || '',
      email: existingCoParent?.email || '',
      street_name: existingCoParent?.street_name || '',
      street_number: existingCoParent?.street_number || '',
      city: existingCoParent?.city || '',
      zip_code: existingCoParent?.zip_code || '',
      vat_number: existingCoParent?.vat_number || '',
      social_security_number: existingCoParent?.social_security_number || '',
    }
  })

  const validateCoParentData = (data: CoParentFormData): boolean => {
    let isValid = true;

    if (parent) {
      if (data.first_name && data.last_name && 
          data.first_name.trim().toLowerCase() === parent.first_name.trim().toLowerCase() && 
          data.last_name.trim().toLowerCase() === parent.last_name.trim().toLowerCase()) {
        setError('first_name', { 
          type: 'manual', 
          message: t('validation.coParentNameMatch', { ns: 'profile' }) 
        });
        isValid = false;
      }

      if (data.email && data.email.trim() !== '' && 
          data.email.trim().toLowerCase() === parent.email.trim().toLowerCase()) {
        setError('email', { 
          type: 'manual', 
          message: t('validation.coParentEmailMatch', { ns: 'profile' }) 
        });
        isValid = false;
      }

      if (data.vat_number && data.vat_number.trim() !== '' && parent.vat_number && 
          data.vat_number.trim() === parent.vat_number.trim()) {
        setError('vat_number', { 
          type: 'manual', 
          message: t('validation.coParentVatMatch', { ns: 'profile' }) 
        });
        isValid = false;
      }

      if (data.social_security_number && data.social_security_number.trim() !== '' && 
          parent.social_security_number && 
          data.social_security_number.trim() === parent.social_security_number.trim()) {
        setError('social_security_number', { 
          type: 'manual', 
          message: t('validation.coParentSsnMatch', { ns: 'profile' }) 
        });
        isValid = false;
      }
    }

    return isValid;
  }

  const submitForm = async (data: CoParentFormData) => {
    setIsLoading(true)
    setErrorMessage(null)

    if (!validateCoParentData(data)) {
      setIsLoading(false);
      return;
    }

    try {
      let response;
      
      const cleanPayload: Partial<CoParentFormData> = {
        first_name: data.first_name,
        last_name: data.last_name,
      };
      
      if (data.email && data.email.trim() !== '') cleanPayload.email = data.email.trim();
      if (data.street_name && data.street_name.trim() !== '') cleanPayload.street_name = data.street_name.trim();
      if (data.street_number && data.street_number.trim() !== '') cleanPayload.street_number = data.street_number.trim();
      if (data.city && data.city.trim() !== '') cleanPayload.city = data.city.trim();
      if (data.zip_code && data.zip_code.trim() !== '') cleanPayload.zip_code = data.zip_code.trim();
      if (data.vat_number && data.vat_number.trim() !== '') cleanPayload.vat_number = data.vat_number.trim();
      if (data.social_security_number && data.social_security_number.trim() !== '') cleanPayload.social_security_number = data.social_security_number.trim();
      
      if (!data.email || data.email.trim() === '') {
        response = await client.post('/direct-coparent-create/', cleanPayload);
      } else if (existingCoParent) {
        response = await client.patch(`/co-parents/${existingCoParent.id}/`, cleanPayload);
      } else {
        response = await client.post('/co-parents/', cleanPayload);
      }
      
      onSuccess(response.data);
    } catch (err: unknown) {
      const error = err as any;
      console.error('Error creating/updating co-parent:', error.response?.data || (error as Error).message);
      
      let hasFieldErrors = false;
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.email) {
          setError('email', { 
            type: 'manual', 
            message: errorData.email[0]
          });
          hasFieldErrors = true;
        }
        
        if (errorData.vat_number) {
          if (errorData.vat_number[0].includes('9 digits')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatFormat', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('check digit')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatInvalid', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('already in use')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatExists', { ns: 'profile' }) 
            });
          }
          hasFieldErrors = true;
        }
        
        if (errorData.social_security_number) {
          if (errorData.social_security_number[0].includes('11 digits')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnFormat', { ns: 'profile' }) 
            });
          } else if (errorData.social_security_number[0].includes('already in use')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnExists', { ns: 'profile' }) 
            });
          }
          hasFieldErrors = true;
        }
        
        if (!hasFieldErrors) {
          if (errorData.detail) {
            setErrorMessage(errorData.detail);
          } else {
            setErrorMessage(t('errors.coParentSubmit', { ns: 'profile' }));
          }
        }
      } else {
        setErrorMessage(t('errors.coParentSubmit', { ns: 'profile' }));
      }
    } finally {
      setIsLoading(false);
    }
  }

  // Add useEffect for scrolling and highlighting
  useEffect(() => {
    if (highlightSsn) {
      // Small delay to ensure the component is rendered
      const timer = setTimeout(() => {
        const ssnInput = document.getElementById('coparent_social_security_number');
        if (ssnInput) {
          ssnInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          ssnInput.classList.add('highlight-field');
          // Remove highlight after 3 seconds
          setTimeout(() => {
            ssnInput.classList.remove('highlight-field');
          }, 3000);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightSsn]);

  const formContent = (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block font-bold mb-1" htmlFor="first_name">
            {t('fields.firstName', { ns: 'profile' })} *
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="first_name"
            {...register('first_name', { required: t('validation.firstNameRequired', { ns: 'profile' }) })}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.firstNamePlaceholder', { ns: 'profile' })}
          />
          {errors.first_name && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.first_name.message}</p>
          )}
        </div>

        <div>
          <label className="block font-bold mb-1" htmlFor="last_name">
            {t('fields.lastName', { ns: 'profile' })} *
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="last_name"
            {...register('last_name', { required: t('validation.lastNameRequired', { ns: 'profile' }) })}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.lastNamePlaceholder', { ns: 'profile' })}
          />
          {errors.last_name && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <label className="block font-bold mb-2" htmlFor="email">
          {t('fields.email', { ns: 'profile' })}
          <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
        </label>
        <p className="text-gray-500 text-sm mb-1">
          {t('coParent.autoGenerated', { ns: 'profile' })}
        </p>
        <input
          type="email"
          id="email"
          {...register('email')}
          className="w-full p-2 border-2 border-black"
          placeholder={t('fields.emailPlaceholder', { ns: 'profile' })}
        />
        {errors.email && (
          <p className="text-neobrutalism-red mt-1 font-bold">{errors.email.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block font-bold mb-1" htmlFor="street_name">
            {t('fields.streetName', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="street_name"
            {...register('street_name')}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.streetNamePlaceholder', { ns: 'profile' })}
          />
          {errors.street_name && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.street_name.message}</p>
          )}
        </div>

        <div>
          <label className="block font-bold mb-1" htmlFor="street_number">
            {t('fields.streetNumber', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="street_number"
            {...register('street_number')}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.streetNumberPlaceholder', { ns: 'profile' })}
          />
          {errors.street_number && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.street_number.message}</p>
          )}
        </div>

        <div>
          <label className="block font-bold mb-1" htmlFor="city">
            {t('fields.city', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="city"
            {...register('city')}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.cityPlaceholder', { ns: 'profile' })}
          />
          {errors.city && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.city.message}</p>
          )}
        </div>

        <div>
          <label className="block font-bold mb-1" htmlFor="zip_code">
            {t('fields.zipCode', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="zip_code"
            {...register('zip_code')}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.zipCodePlaceholder', { ns: 'profile' })}
          />
          {errors.zip_code && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.zip_code.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block font-bold mb-1" htmlFor="vat_number">
            {t('fields.vatNumber', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="vat_number"
            {...register('vat_number', { 
              pattern: {
                value: /^\d{9}$/,
                message: t('validation.vatFormat', { ns: 'profile' })
              }
            })}
            className="w-full p-2 border-2 border-black"
            placeholder={t('fields.vatNumberPlaceholder', { ns: 'profile' })}
          />
          {errors.vat_number && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.vat_number.message}</p>
          )}
        </div>

        <div>
          <label className="block font-bold mb-1" htmlFor="coparent_social_security_number">
            {t('fields.socialSecurityNumber', { ns: 'profile' })}
            <span className="text-gray-500 text-sm italic ml-1">{t('fields.coParentSuffix', { ns: 'profile' })}</span>
          </label>
          <input
            type="text"
            id="coparent_social_security_number"
            {...register('social_security_number', { 
              pattern: {
                value: /^\d{11}$/,
                message: t('validation.ssnFormat', { ns: 'profile' })
              }
            })}
            className={`w-full p-2 border-2 border-black ${highlightSsn ? 'highlight-field' : ''}`}
            placeholder={t('fields.socialSecurityNumberPlaceholder', { ns: 'profile' })}
          />
          {errors.social_security_number && (
            <p className="text-neobrutalism-red mt-1 font-bold">{errors.social_security_number.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-4 mt-6">
        <button
          type={isNested ? "button" : "submit"}
          onClick={isNested ? handleSubmit(submitForm) : undefined}
          className="text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 bg-yellow-500 border-2 border-black font-bold text-white"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('actions.saving', { ns: 'profile' })}
            </span>
          ) : (
            existingCoParent ? t('actions.update', { ns: 'profile' }) : t('actions.save', { ns: 'profile' })
          )}
        </button>
      </div>
    </>
  )

  return (
    <div className="co-parent-form w-full bg-yellow-100 p-4 border-4 border-yellow-500">
      <h3 className={`text-xl font-bold ${isNested ? 'mb-2' : 'mb-4'}`}>
        {existingCoParent ? t('coParent.edit.title', { ns: 'profile' }) : t('coParent.add.title', { ns: 'profile' })}
      </h3>
      
      {errorMessage && (
        <div className="bg-neobrutalism-red p-3 rounded-lg border-2 border-black mb-3">
          <p className="text-neobrutalism-text font-bold text-sm">{errorMessage}</p>
        </div>
      )}

      {isNested ? (
        <div className="space-y-4 w-full">
          {formContent}
        </div>
      ) : (
        <form onSubmit={handleSubmit(submitForm)} className="space-y-4 w-full">
          {formContent}
        </form>
      )}
    </div>
  )
} 